import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { sqlReadRaw as sqlRead, payloadOf } from "../../utils/sqlClient";
import {
  computeAhuZoneRollup, computePlantEquipmentRollup,
  computeCoilLoads, computeHumidifierCapacityLbHr, computeFanMotorSizing,
} from "../../utils/hvacLoadEngine";
import { equipmentHeatGainByRoom } from "../../utils/equipmentLoads";
import { nonBaseEquipmentIds } from "../../utils/designScenarios";
import DesignScenariosPanel from "./DesignScenariosPanel";

// Mechanical Systems — the Room -> AHU -> central plant equipment hierarchy
// (db/21_room_takeoff.sql's TakeoffAHUZones + db/24_process_equipment.sql's
// ProcessEquipment), and the live load/CFM/coil/humidifier/fan rollup
// (src/utils/hvacLoadEngine.js) that reads it. Everything below is recomputed
// on every render from stored inputs only — no "regenerate" step, matching
// how calcRoom()/estimatePricing.js already work in this app.
//
// AHU zones are edited here as "AHU-1 serves rooms A/B/C, Qty 1"; plant
// equipment (chiller/boiler/pump package) is a ProcessEquipment row that one
// or more zones point at via ServedByEquipmentID — e.g. one chiller-and-pump
// package (Qty 2, Standby 1: "N+1") serving three AHU zones.
//
// Ownership (P2-A, 2026-09-12): ProcessEquipment is the project's canonical
// assumed-equipment model, shared by HVAC loads, electrical connections,
// process utilities, controls, placement, schedules and proposal assumptions.
// This panel edits the mechanical subset of those SHARED records for AHU /
// plant purposes; the full record (manufacturer, room, status, connections)
// is managed on the Assumed Equipment tab. It does not own the data.

const AHU_CATEGORIES = ["ahu", "dx_unit"];
const PLANT_CATEGORIES = ["chiller", "boiler", "pump_package", "condensing_unit"];
const CATEGORY_LABEL = { ahu: "AHU", dx_unit: "DX Unit", chiller: "Chiller", boiler: "Boiler", pump_package: "Pump Package", condensing_unit: "Condensing Unit" };

const fmt = (n, d = 0) => (n === null || n === undefined || isNaN(n)) ? "—"
  : Number(n).toLocaleString("en-US", { maximumFractionDigits: d, minimumFractionDigits: d });

const th = { fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textAlign: "right", padding: "4px 8px", textTransform: "uppercase", letterSpacing: "0.03em" };
const td = { fontSize: 12.5, textAlign: "right", padding: "4px 8px", borderTop: "1px solid var(--cs-border)" };
const inputSm = { width: "100%", fontSize: 12, textAlign: "right", border: "1px solid var(--cs-border)", borderRadius: 5, padding: "3px 6px", background: "var(--surface)", color: "var(--text)" };

// Module-scoped (not defined inside the panel) so its identity is stable
// across parent re-renders — an inline component here would remount on every
// keystroke elsewhere on the page, wiping out draft edits mid-type.
function EquipmentRow({ eq, isLocked, editing, onEdit, onCancel, onSave, onDelete }) {
  const [draft, setDraft] = useState(eq);
  useEffect(() => { if (editing) setDraft(eq); }, [editing, eq]);

  if (!editing) {
    return (
      <tr>
        <td style={{ ...td, textAlign: "left" }}>{eq.Tag || "—"}</td>
        <td style={{ ...td, textAlign: "left" }}>{eq.Description}</td>
        <td style={{ ...td, textAlign: "left" }}>{CATEGORY_LABEL[eq.Category] || eq.Category || "—"}</td>
        <td style={td}>{eq.Qty}</td>
        <td style={td}>{eq.StandbyUnits || 0}</td>
        <td style={{ ...td, textAlign: "right" }}>
          {!isLocked && (
            <>
              <button onClick={onEdit} style={{ fontSize: 11, marginRight: 6, cursor: "pointer" }}>Edit</button>
              <button onClick={() => onDelete(eq)} style={{ fontSize: 11, cursor: "pointer", color: "#b91c1c" }}>Remove</button>
            </>
          )}
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td style={td}><input style={inputSm} value={draft.Tag || ""} onChange={e => setDraft({ ...draft, Tag: e.target.value })} /></td>
      <td style={td}><input style={{ ...inputSm, textAlign: "left" }} value={draft.Description || ""} onChange={e => setDraft({ ...draft, Description: e.target.value })} /></td>
      <td style={td}>
        <select style={inputSm} value={draft.Category || ""} onChange={e => setDraft({ ...draft, Category: e.target.value })}>
          {[...AHU_CATEGORIES, ...PLANT_CATEGORIES].map(c => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
        </select>
      </td>
      <td style={td}><input type="number" min={1} style={inputSm} value={draft.Qty ?? 1} onChange={e => setDraft({ ...draft, Qty: +e.target.value })} /></td>
      <td style={td}><input type="number" min={0} style={inputSm} value={draft.StandbyUnits ?? 0} onChange={e => setDraft({ ...draft, StandbyUnits: +e.target.value })} /></td>
      <td style={{ ...td, textAlign: "right" }}>
        <button onClick={() => onSave(draft)} style={{ fontSize: 11, marginRight: 6, cursor: "pointer", fontWeight: 700 }}>Save</button>
        <button onClick={onCancel} style={{ fontSize: 11, cursor: "pointer" }}>Cancel</button>
      </td>
    </tr>
  );
}

// systemsCtx = { bodAttrs, tunables } for roomSystemsEngine.js — the
// lighting heat-gain term now reads the EFFECTIVE fixture count × the selected
// fixture's real wattage (Basis of Design), with VaPerLightingFixture only as
// the fallback. Built by RoomTakeoff.jsx; settings come from elecSettings here.
export default function MechanicalSystemsPanel({ activeRevId, isLocked, rooms, zones, criteria, saveZone, classifications = [], systemsCtx = {}, scenarioDispositions = null, onScenarioRowsChange = null, currentUserEmail = null }) {
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [connections, setConnections] = useState([]);
  const [elecSettings, setElecSettings] = useState(null);
  const [editingEquipmentId, setEditingEquipmentId] = useState(null);
  const [newEquipment, setNewEquipment] = useState({ category: "ahu", description: "" });
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetEquipmentRegister @revision_id=${activeRevId}`)
      .then(result => {
        setEquipment(result?.Table1 || []);
        setConnections(result?.Table2 || []);
      })
      .catch(e => console.error("Equipment register load failed:", e));
    sqlRead(`EXEC sp_GetEstimateCostSettings @revision_id=${activeRevId}`)
      .then(result => setElecSettings((result?.Table1 || [])[0] || null))
      .catch(e => console.error("Electrical settings load failed (lighting heat gain unavailable):", e));
  }, [activeRevId]);

  useEffect(() => { load(); }, [load]);

  async function saveEquipment(eq) {
    if (isLocked) return;
    try {
      const payload = payloadOf({
        equipment_id: eq.EquipmentID ?? null,
        revision_id:  activeRevId,
        tag:          eq.Tag || null,
        description:  eq.Description,
        category:     eq.Category || null,
        manufacturer: eq.Manufacturer || null,
        model:        eq.Model || null,
        qty:          eq.Qty ?? 1,
        standby_units: eq.StandbyUnits ?? 0,
        location_text: eq.LocationText || null,
      });
      await sqlRead(`EXEC sp_SaveProcessEquipment @payload = '${payload}'`);
      setEditingEquipmentId(null);
      setAdding(false);
      setNewEquipment({ category: "ahu", description: "" });
      load();
    } catch (e) { alert("Error saving equipment: " + e.message); }
  }

  async function deleteEquipment(eq) {
    if (isLocked) return;
    if (!window.confirm(`Remove "${eq.Description}"? Any AHU zone pointing at it as plant equipment will need reassigning.`)) return;
    try {
      const payload = payloadOf({ equipment_id: eq.EquipmentID, revision_id: activeRevId, is_delete: 1 });
      await sqlRead(`EXEC sp_SaveProcessEquipment @payload = '${payload}'`);
      load();
    } catch (e) { alert("Error removing equipment: " + e.message); }
  }

  function saveZoneField(zone, patch) {
    const next = { ...zone, ...patch };
    saveZone(next);
  }

  // ── Live rollup (Room -> AHU zone -> plant equipment) ─────────────────────────
  const zonesById = Object.fromEntries(zones.map(z => [z.AHUZoneID, z]));
  const equipmentById = Object.fromEntries(equipment.map(e => [e.EquipmentID, e]));
  // Equipment heat per room through the P2-B load contract
  // (src/utils/equipmentLoads.js — the formula lives there, not here):
  // explicit BTUH, else kW × usage factor × 3412.142 (derived/assumed), HP
  // alone unresolved (0 + warning), × active units (Qty − StandbyUnits).
  // Before 2026-09-12 only the explicit value was summed here.
  // Scenarios Phase J-3: the zone loads describe the BASE design — a proposal
  // alternate's or an unpublished scenario's plant record adds no room heat
  // here (named below, never silently dropped). Demand stays shared by every
  // scenario; only the plant of the non-base solutions is kept out.
  const equipmentHeat = equipmentHeatGainByRoom(equipment, connections, { excludeEquipmentIds: nonBaseEquipmentIds(scenarioDispositions) });
  const heatGainByRoomId = equipmentHeat.byRoomBtuh;
  const ahuRollups = computeAhuZoneRollup(rooms, criteria, criteria, elecSettings, zonesById, heatGainByRoomId, systemsCtx);
  const plantRollups = computePlantEquipmentRollup(ahuRollups, equipmentById);

  const ahuEquipment = equipment.filter(e => AHU_CATEGORIES.includes(e.Category));
  const plantEquipment = equipment.filter(e => PLANT_CATEGORIES.includes(e.Category));
  const otherEquipment = equipment.filter(e => !AHU_CATEGORIES.includes(e.Category) && !PLANT_CATEGORIES.includes(e.Category));

  return (
    <div>
      {equipmentHeat.excluded.length > 0 && (
        <div data-testid="heat-excluded-plant" style={{ fontSize: 11.5, color: "var(--text3)", marginBottom: 6 }}>
          Not in room loads (plant of a non-base design scenario): {equipmentHeat.excluded.map(x => `${x.tag}${x.btuh > 0 ? ` (${Math.round(x.btuh).toLocaleString()} BTUH)` : ""}`).join(", ")}.
        </div>
      )}
      {equipmentHeat.warnings.length > 0 && (
        <div style={{ fontSize: 11.5, color: "#92400e", background: "var(--tint-amber)", border: "1px solid #fde68a", borderRadius: 6, padding: "6px 10px", marginBottom: 10 }}
          title={equipmentHeat.warnings.map(w => w.message).join("\n")}>
          ⚠️ {equipmentHeat.warnings.length} equipment heat basis warning{equipmentHeat.warnings.length === 1 ? "" : "s"} — {" "}
          {[...new Set(equipmentHeat.warnings.map(w => w.code))].join(", ")}. Room heat counts only explicit BTUH or kW × usage factor; HP-only
          connections and missing usage factors need a value on the Assumed Equipment tab (hover for details).
        </div>
      )}
      <div className="panel-sub" style={{ marginBottom: 12 }}>
        Budgetary HVAC sizing derived live from the room schedule and Discipline Criteria —
        every number below recalculates instantly as rooms, criteria, or equipment change.
        This approximates (does not replace) a full engineering calc: see the room-by-room
        breakdown for which method (ACH / heat gain / exhaust) governs each room's CFM.
      </div>

      {/* ── Cleanroom Classification reference table (drives ACH) ──────────────── */}
      {/* Read-only here since 2026-09-09 — this is a global, not per-project,
          source of truth (db/43_cleanroom_classifications.sql), and it's now
          administered from Setup > Basis of Design instead, alongside every
          other Discipline Criteria master option list. This page still shows
          it for context while picking a room's Class. */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Cleanroom Classification</div>
          <button onClick={() => navigate("/basis-of-design")} style={{ fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>Manage in Setup → Basis of Design</button>
        </div>
        <div className="panel-sub" style={{ marginBottom: 6 }}>
          ISPE/EU/FDA grade correspondence — picking a classification in the room schedule's
          "Class" column populates that room's ACH from Target ACH here (editable per room
          afterward). Min Accepted is reference only; it doesn't drive anything.
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: "left" }}>ISPE</th>
              <th style={{ ...th, textAlign: "left" }}>EU</th>
              <th style={{ ...th, textAlign: "left" }}>FDA</th>
              <th style={th}>Min Accepted</th>
              <th style={th}>Target</th>
              <th style={th}>EU At-Rest</th>
              <th style={th}>FDA In-Op</th>
            </tr>
          </thead>
          <tbody>
            {classifications.filter(row => row.IsActive).map(row => (
              <tr key={row.ClassificationID}>
                <td style={{ ...td, textAlign: "left" }}>{row.ISPEGrade || "—"}</td>
                <td style={{ ...td, textAlign: "left" }}>{row.EUGrade || "—"}</td>
                <td style={{ ...td, textAlign: "left" }}>{row.FDAGrade || "—"}</td>
                <td style={td}>{row.MinACH ?? "—"} {row.MinACHUnit}</td>
                <td style={{ ...td, fontWeight: 700 }}>{row.TargetACH ?? "—"} {row.TargetACHUnit}</td>
                <td style={td}>{row.EuAtRestParticles || "—"}</td>
                <td style={td}>{row.FdaInOperationParticles || "—"}</td>
              </tr>
            ))}
            {classifications.filter(row => row.IsActive).length === 0 && (
              <tr><td colSpan={7} style={{ ...td, textAlign: "center", color: "var(--text3)" }}>No classifications yet — add one in Setup → Basis of Design.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Equipment: AHUs + central plant ──────────────────────────────────── */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Equipment</div>
          {!isLocked && !adding && (
            <button onClick={() => setAdding(true)} style={{ fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>+ Add equipment</button>
          )}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: "left" }}>Tag</th>
              <th style={{ ...th, textAlign: "left" }}>Description</th>
              <th style={{ ...th, textAlign: "left" }}>Category</th>
              <th style={th}>Qty</th>
              <th style={th}>Standby</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {[...ahuEquipment, ...plantEquipment, ...otherEquipment].map(eq => (
              <EquipmentRow
                key={eq.EquipmentID}
                eq={eq}
                isLocked={isLocked}
                editing={editingEquipmentId === eq.EquipmentID}
                onEdit={() => setEditingEquipmentId(eq.EquipmentID)}
                onCancel={() => setEditingEquipmentId(null)}
                onSave={saveEquipment}
                onDelete={deleteEquipment}
              />
            ))}
            {adding && (
              <tr>
                <td style={td}></td>
                <td style={td}><input style={{ ...inputSm, textAlign: "left" }} autoFocus placeholder="e.g. Chiller-1" value={newEquipment.description} onChange={e => setNewEquipment({ ...newEquipment, description: e.target.value })} /></td>
                <td style={td}>
                  <select style={inputSm} value={newEquipment.category} onChange={e => setNewEquipment({ ...newEquipment, category: e.target.value })}>
                    {[...AHU_CATEGORIES, ...PLANT_CATEGORIES].map(c => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
                  </select>
                </td>
                <td style={td}>1</td>
                <td style={td}>0</td>
                <td style={{ ...td, textAlign: "right" }}>
                  <button
                    disabled={!newEquipment.description.trim()}
                    onClick={() => saveEquipment(newEquipment)}
                    style={{ fontSize: 11, marginRight: 6, cursor: "pointer", fontWeight: 700 }}
                  >Save</button>
                  <button onClick={() => setAdding(false)} style={{ fontSize: 11, cursor: "pointer" }}>Cancel</button>
                </td>
              </tr>
            )}
            {equipment.length === 0 && !adding && (
              <tr><td colSpan={6} style={{ ...td, textAlign: "center", color: "var(--text3)" }}>No AHU or plant equipment yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── AHU zones: assign rooms' zones to plant equipment + redundancy ────── */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>AHU Zones</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: "left" }}>Zone</th>
              <th style={th}>Qty</th>
              <th style={th}>Standby</th>
              <th style={{ ...th, textAlign: "left" }}>Served by</th>
              <th style={th}>Outside air %</th>
            </tr>
          </thead>
          <tbody>
            {zones.map(zone => (
              <tr key={zone.AHUZoneID}>
                <td style={{ ...td, textAlign: "left" }}>{zone.ZoneName}</td>
                <td style={td}>
                  <input type="number" min={1} disabled={isLocked} style={inputSm} defaultValue={zone.Qty ?? 1}
                    onBlur={e => { const v = +e.target.value; if (v !== (zone.Qty ?? 1)) saveZoneField(zone, { Qty: v }); }} />
                </td>
                <td style={td}>
                  <input type="number" min={0} disabled={isLocked} style={inputSm} defaultValue={zone.StandbyUnits ?? 0}
                    onBlur={e => { const v = +e.target.value; if (v !== (zone.StandbyUnits ?? 0)) saveZoneField(zone, { StandbyUnits: v }); }} />
                </td>
                <td style={td}>
                  <select
                    disabled={isLocked}
                    style={{ ...inputSm, textAlign: "left" }}
                    value={zone.ServedByEquipmentID ?? ""}
                    onChange={e => saveZoneField(zone, { ServedByEquipmentID: e.target.value ? +e.target.value : null })}
                  >
                    <option value="">— none —</option>
                    {plantEquipment.map(eq => <option key={eq.EquipmentID} value={eq.EquipmentID}>{eq.Tag || eq.Description}</option>)}
                  </select>
                </td>
                <td style={td}>
                  <input type="number" min={0} max={100} disabled={isLocked} style={inputSm}
                    defaultValue={zone.OutsideAirPct != null ? Math.round(zone.OutsideAirPct * 100) : (zone.AHUType === "oa100" ? 100 : 30)}
                    onBlur={e => { const v = +e.target.value / 100; saveZoneField(zone, { OutsideAirPct: v }); }} />
                </td>
              </tr>
            ))}
            {zones.length === 0 && (
              <tr><td colSpan={5} style={{ ...td, textAlign: "center", color: "var(--text3)" }}>No AHU zones yet — add one from the room schedule.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Live rollup: Room -> AHU -> plant ──────────────────────────────────── */}
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Load / CFM Rollup</div>
      {ahuRollups.map(ahu => {
        const outsideAirPct = ahu.zone?.OutsideAirPct ?? (ahu.zone?.AHUType === "oa100" ? 1 : 0.3);
        const coils = computeCoilLoads(ahu.totalCfm, outsideAirPct, criteria);
        const humidifier = computeHumidifierCapacityLbHr(coils.oaCfm, criteria);
        const fan = computeFanMotorSizing(ahu.totalCfm, criteria);
        return (
          <div key={ahu.AHUZoneID} style={{ border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: 10, marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700 }}>{ahu.zone?.ZoneName || `Zone ${ahu.AHUZoneID}`}</div>
              <div style={{ fontSize: 11, color: "var(--text3)" }}>
                {fmt(ahu.totalCfm)} CFM total · {fmt(ahu.tonnage, 1)} tons
                {ahu.activeUnits > 1 ? ` · ${fmt(ahu.perUnitCfm)} CFM / unit (${ahu.activeUnits} active)` : ""}
              </div>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 6 }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: "left" }}>Room</th>
                  <th style={th}>ACH CFM</th>
                  <th style={th}>Heat-gain CFM</th>
                  <th style={th}>Exhaust CFM</th>
                  <th style={th}>Final CFM</th>
                  <th style={{ ...th, textAlign: "left" }}>Governs</th>
                </tr>
              </thead>
              <tbody>
                {ahu.rooms.map(({ room, cfm }) => (
                  <tr key={room.RoomID}>
                    <td style={{ ...td, textAlign: "left" }}>{room.RoomName}</td>
                    <td style={td}>{fmt(cfm.achCfm)}</td>
                    <td style={td}>{fmt(cfm.heatGainCfm)}</td>
                    <td style={td}>{fmt(cfm.exhaustCfm)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{fmt(cfm.finalCfm)}</td>
                    <td style={{ ...td, textAlign: "left", textTransform: "capitalize" }}>{cfm.governingMethod === "ach" ? "ACH" : cfm.governingMethod === "heatGain" ? "Heat gain" : "Exhaust"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 11.5, color: "var(--text2)" }}>
              <span><b>Preheat:</b> {fmt(coils.preheatBtuh)} BTUH</span>
              <span><b>Reheat:</b> {fmt(coils.reheatBtuh)} BTUH</span>
              <span><b>Humidifier:</b> {fmt(humidifier, 1)} lb/hr</span>
              <span><b>Fan:</b> {fmt(fan.bhp, 1)} BHP → {fan.motorHp} HP motor</span>
            </div>
          </div>
        );
      })}
      {ahuRollups.length === 0 && (
        <div className="panel-sub">No rooms are assigned to an AHU zone yet.</div>
      )}

      {plantRollups.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Central Plant Equipment <span style={{ fontWeight: 400, fontSize: 11, color: "var(--text3)" }}>(live rollup via each zone's plant pointer — the selected-base scenario will drive this in a later phase)</span></div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: "left" }}>Equipment</th>
                <th style={{ ...th, textAlign: "left" }}>Serves</th>
                <th style={th}>Total CFM</th>
                <th style={th}>Total tons</th>
                <th style={th}>Active units</th>
                <th style={th}>Per-unit tons</th>
              </tr>
            </thead>
            <tbody>
              {plantRollups.map(p => (
                <tr key={p.EquipmentID}>
                  <td style={{ ...td, textAlign: "left" }}>{p.equipment?.Tag || p.equipment?.Description || `#${p.EquipmentID}`}</td>
                  <td style={{ ...td, textAlign: "left" }}>{p.zones.map(z => z.zone?.ZoneName).join(", ")}</td>
                  <td style={td}>{fmt(p.totalCfm)}</td>
                  <td style={td}>{fmt(p.tonnage, 1)}</td>
                  <td style={td}>{p.activeUnits}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{fmt(p.perUnitTonnage, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* System Architecture & Scenarios Phase B — records only; demand = the ahuRollups computed above, shared by every scenario. */}
      <DesignScenariosPanel activeRevId={activeRevId} isLocked={isLocked} zones={zones} ahuRollups={ahuRollups} equipment={equipment} criteria={criteria} tunables={systemsCtx?.tunables || {}} settings={elecSettings} rooms={rooms} onRowsChange={onScenarioRowsChange} reviewedBy={currentUserEmail} onEquipmentChanged={load} />
    </div>
  );
}
